// =============================================================================
// Location Types for Locations Page
// Data source: PostgreSQL via GET /v4/locations API
// =============================================================================

/**
 * Location data from the V4 API (PostgreSQL)
 */
export interface Location {
  id: string; // UUID
  name: string;
  searchName?: string;
  abbreviations?: string[];
  roomNumber?: string;
  tags?: string[];
  disabled?: boolean;

  // Contact info
  phone?: string;
  extension?: string;
  fax?: string;
  faxExtension?: string;

  // Geolocation
  latitude?: number;
  longitude?: number;

  // Related entities (MongoDB ObjectId arrays)
  sensorIds?: string[];
  transmitterIds?: string[];

  // Enriched names (resolved from MongoDB)
  usergroupName?: string | null;
  transmitterNames?: string[];
  sensorNames?: string[];

  // Image
  mongoImageId?: string;

  // Account info (from JOIN)
  accountId: string;
  accountName?: string;
  accountNumber?: string;

  // Site info (from JOIN)
  siteId?: string;
  siteName?: string;

  // MongoDB references (for backward compatibility during migration)
  mongoLocationId?: string;
  mongoSiteId?: string;
  mongoUsergroupId?: string;

  // Timestamps
  createdAt?: string;
  updatedAt?: string;
  description?: string;

  // Legacy fields for backward compatibility with V3 responses
  _id?: string;
  organisation?: string | PopulatedOrganisation;
  site?: string | PopulatedSite;
  usergroup?: string | PopulatedUsergroup;
  search_name?: string;
  room_number?: string;
  fax_extension?: string;
  coordinates?: [number, number];
  sensors?: string[];
  transmitters?: string[];
  image?: string;
  created_at?: string;
}

/**
 * Populated organisation data (V3 legacy)
 */
export interface PopulatedOrganisation {
  _id: string;
  name: string;
}

/**
 * Populated site data (V3 legacy)
 */
export interface PopulatedSite {
  _id: string;
  name: string;
}

/**
 * Populated usergroup data (V3 legacy)
 */
export interface PopulatedUsergroup {
  _id: string;
  name: string;
}

/**
 * Filters for querying locations
 */
export interface LocationsFilters {
  search?: string;
  accountId?: string;
  siteId?: string;
  tags?: string[];
  disabled?: boolean;
  sortKey?: 'name' | 'roomNumber' | 'disabled' | 'createdAt' | 'siteName' | 'accountName' | 'coordinates';
  sortDirection?: 1 | -1;
  pageNumber?: number;
  pageLength?: number;
}

/**
 * API response for GET /v4/locations
 */
export interface V4LocationsResponse {
  locations: Location[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Paginated response wrapper
 */
export interface LocationsResponse {
  locations: Location[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Account for dropdown filter (V4)
 */
export interface Account {
  id: string;
  name: string;
  accountNumber?: string;
  mongoOrganisationId?: string;
}

/**
 * Paginated account response for async account dropdowns
 */
export interface LocationAccountsResponse {
  accounts: Account[];
  pageNumber: number;
  pageLength: number;
  total: number;
}

/**
 * Site option for dropdown (V4)
 */
export interface SiteOption {
  id: string;
  name: string;
  mongoSiteId?: string;
}

/**
 * Options response for location form
 */
export interface LocationOptions {
  sites: SiteOption[];
}

/**
 * Input for creating a new location (V4)
 */
export interface CreateLocationInput {
  accountId: string;
  siteId?: string;
  name: string;
  searchName?: string;
  abbreviations?: string[];
  roomNumber?: string;
  tags?: string[];
  phone?: string;
  extension?: string;
  fax?: string;
  faxExtension?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  transmitters?: string[];
  sensors?: string[];
}

/**
 * Input for updating a location (V4)
 */
export interface UpdateLocationInput {
  name?: string;
  searchName?: string;
  abbreviations?: string[];
  roomNumber?: string;
  siteId?: string | null;
  tags?: string[];
  disabled?: boolean;
  phone?: string;
  extension?: string;
  fax?: string;
  faxExtension?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  // New fields for equipment and access control
  usergroup?: string | null; // MongoDB ObjectId
  transmitters?: string[]; // Array of MongoDB ObjectIds
  sensors?: string[]; // Array of MongoDB ObjectIds
}

// =============================================================================
// Form Options Types (for location create/edit modal)
// =============================================================================

/**
 * Transmitter data for form selection
 */
export interface Transmitter {
  _id: string;
  name?: string;
  dev_id?: string;
  customid?: string;
  battery_voltage?: number;
  battery_percentage?: number;
}

/**
 * Sensor data for form selection
 */
export interface Sensor {
  _id: string;
  name: string;
  search_name?: string;
}

/**
 * User group data for form selection
 */
export interface Usergroup {
  _id: string;
  name: string;
  tags?: string[];
}

/**
 * Response from GET /v4/locations/form-options
 */
export interface LocationFormOptions {
  usergroups: Usergroup[];
  transmitters: Transmitter[];
  sensors: Sensor[];
}

// =============================================================================
// Changelog Types
// =============================================================================

/**
 * A single field change within a changelog entry
 */
export interface ChangelogChange {
  field: string;
  old?: unknown;
  new?: unknown;
}

/**
 * A changelog entry representing a modification to a location
 */
export interface ChangelogEntry {
  _id?: string;
  timestamp: string;
  user: {
    firstname?: string;
    lastname?: string;
  } | null;
  isOriginal: boolean;
  changes: ChangelogChange[];
}

/**
 * Response from GET /v4/locations/:id/changelog
 */
export interface LocationChangelogResponse {
  changelog: ChangelogEntry[];
}

// =============================================================================
// Image Types
// =============================================================================

/**
 * Response from POST /v4/locations/:id/image
 */
export interface LocationImageUploadResponse {
  success: boolean;
  imageId: string;
}

/**
 * Response from DELETE /v4/locations/:id/image
 */
export interface LocationImageDeleteResponse {
  success: boolean;
}

/**
 * Known location tags
 */
export const LOCATION_TAGS = [
  'storage',
  'lab',
  'office',
  'production',
  'warehouse',
  'clean room',
  'cold storage',
  'freezer',
  'incubator',
] as const;

export type LocationTag = (typeof LOCATION_TAGS)[number];
